import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ScrollView } from "react-native";
import i18n from "@microdoing/shared/i18n";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import { changeUserInfo } from "@microdoing/shared/services/profileSettings/actions";
import {
  Box,
  MainText,
  MySafeAreaView,
  PressableOpacity,
} from "../../components/common";
import {
  cancelAllNotifications,
  scheduleGoalNotif,
} from "@microdoing/shared/services/notifService/index";
import hasNotificationPermission from "@microdoing/shared/utils/hasNotificationPermission";
import {
  getFirstReminderTime,
  getSecondReminderTime,
  getThirdReminderTime,
  getNumberOfReminders,
} from "@microdoing/shared/services/profileSettings/selectors";
import ReminderDays from "./ReminderDays";
import {
  updateUserWithoutResponseRequest,
  modifyUser,
} from "@microdoing/shared/services/user/actions";
import moment from "moment";
import ReminderHour from "./ReminderHour";
import HeaderScreen from "app/components/common/HeaderScreen";
import { useNavigation } from "@react-navigation/native";
import ValidationModal from "app/components/common/ValidationModal";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const SetRemindersScreen = ({}) => {
  const [isValidationModalOpen, setIsValidationModalOpen] = useState<boolean>(
    false,
  );

  const userId = useSelector(getUserId);
  const firstReminderTime = useSelector(getFirstReminderTime);
  const secondReminderTime = useSelector(getSecondReminderTime);
  const thirdReminderTime = useSelector(getThirdReminderTime);
  const numberOfReminders = useSelector(getNumberOfReminders);

  const insets = useSafeAreaInsets();

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const onPressBack = async () => {
    await cancelAllNotifications();
    await scheduleGoalNotif();

    const hasNotificationsEnabled = await hasNotificationPermission();

    if (!hasNotificationsEnabled) {
      setIsValidationModalOpen(true);
      return;
    }

    dispatch(
      updateUserWithoutResponseRequest({
        userId,
        hasNotificationsEnabled: true,
        hasNotificationsEnabledCheckDate: moment().toDate(),
      }),
    );
    dispatch(modifyUser({ hasNotificationsEnabled }));
    return navigation.goBack();
  };

  const getReminderTime = index => {
    if (index === 0) {
      return firstReminderTime;
    } else if (index === 1) {
      return secondReminderTime;
    }
    return thirdReminderTime;
  };

  const onRemoveReminder = () => {
    if (numberOfReminders > 1) {
      dispatch(changeUserInfo({ numberOfReminders: numberOfReminders - 1 }));
    }
  };

  const onChangeReminderTime = reminderIndex => ({ hour, minute }) => {
    if (reminderIndex === 0) {
      dispatch(changeUserInfo({ firstReminderTime: { hour, minute } }));
    } else if (reminderIndex === 1) {
      dispatch(changeUserInfo({ secondReminderTime: { hour, minute } }));
    } else {
      dispatch(changeUserInfo({ thirdReminderTime: { hour, minute } }));
    }
  };

  const onAddReminder = () => {
    dispatch(changeUserInfo({ numberOfReminders: numberOfReminders + 1 }));
  };

  const getNumberOfRemindersArray = numberOfRemindersData => {
    return new Array(numberOfRemindersData).fill(0);
  };

  const validateNoNotification = () => {
    dispatch(
      updateUserWithoutResponseRequest({
        userId,
        hasNotificationsEnabled: false,
        hasNotificationsEnabledCheckDate: moment().toDate(),
      }),
    );
    dispatch(modifyUser({ hasNotificationsEnabled: false }));

    return navigation.goBack();
  };

  const allowNotifications = () => {
    setIsValidationModalOpen(false);
    return navigation.navigate("AllowNotifications");
  };

  return (
    <Box
      variant="screenView"
      backgroundColor="neutral.white"
      style={{ paddingTop: insets.top }}
    >
      <ValidationModal
        isVisible={isValidationModalOpen}
        title={i18n.t("notifications-not-allowed-title")}
        explanationText={i18n.t("notifications-not-allowed-explanation")}
        rightButtonText={i18n.t("activate")}
        callBackLeftButton={validateNoNotification}
        callBackRightButton={allowNotifications}
        onClickOutside={() => setIsValidationModalOpen(false)}
      />
      <HeaderScreen
        leftIcon
        title={i18n.t("set-my-goals")}
        callbackOnPress={() => onPressBack()}
      />
      <ScrollView>
        <Box flex={1} marginBottom="xm">
          <MainText marginBottom="xm" variant="l4-medium" color="neutral.600">
            {i18n.t("weekly_reminder_schedule_selection")}
          </MainText>
          <MainText variant="l5-bold" color="neutral.900" marginBottom="sm">
            {i18n.t("which-days")}
          </MainText>
          <ReminderDays />
          <MainText
            marginTop="xl"
            variant="l5-bold"
            color="neutral.900"
            marginBottom="sm"
          >
            {i18n.t("time-of-notification")}
          </MainText>
          {getNumberOfRemindersArray(numberOfReminders).map((el, index) => (
            <ReminderHour
              key={index}
              reminderNumber={index + 1}
              reminderTime={getReminderTime(index)}
              onChangeReminderTime={onChangeReminderTime(index)}
              onRemove={onRemoveReminder}
              isLastReminder={index + 1 === numberOfReminders}
            />
          ))}
          {numberOfReminders < 3 && (
            <PressableOpacity onPress={onAddReminder}>
              <MainText
                variant="l4-bold"
                color="brand.500"
                textAlign="center"
                marginTop="l"
              >
                {i18n.t("add-a-reminder")}
              </MainText>
            </PressableOpacity>
          )}
        </Box>
      </ScrollView>
    </Box>
  );
};

export default SetRemindersScreen;
